import React, { useState } from "react";
import { NavLink } from "react-router-dom";
import "./Team.css";
import { ENDPOINTS } from "../api/api";
import useFetch from "../hooks/useFetch";
import { useTranslation } from "react-i18next";
import "../i18n";

// ── Member Card ───────────────────────────────────────────
const MemberCard = ({ member, featured, founderLabel }) => {
  const [hovered, setHovered] = useState(false);

  return (
    <div
      className={`member-card ${featured ? "member-card--featured" : ""} ${hovered ? "member-card--hovered" : ""}`}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {featured && (
        <span className="member-featured-badge">
          <i className="fa-solid fa-star"></i> {founderLabel}
        </span>
      )}

      {/* Avatar */}
      <div className="member-avatar-wrap">
        {member.image ? (
          <img src={member.image} alt={member.name} className="member-avatar-img" />
        ) : (
          <div className="member-avatar-initials">
            <span>{member.initials}</span>
          </div>
        )}
        <div className="member-avatar-ring"></div>
        {featured && <div className="member-avatar-glow"></div>}
      </div>

      {/* Info */}
      <div className="member-info">
        <h3 className="member-name">{member.name}</h3>
        <p className="member-role">{member.role}</p>
        <p className="member-bio">{member.bio}</p>

        <div className="member-skills">
          {(member.skills || []).map((skill) => (
            <span key={skill} className="member-skill-tag">{skill}</span>
          ))}
        </div>

        <div className="member-socials">
          {member.linkedin && (
            <a href={member.linkedin} target="_blank" rel="noopener noreferrer" className="member-social" aria-label="LinkedIn">
              <i className="fa-brands fa-linkedin-in"></i>
            </a>
          )}
          {member.github && (
            <a href={member.github} target="_blank" rel="noopener noreferrer" className="member-social" aria-label="GitHub">
              <i className="fa-brands fa-github"></i>
            </a>
          )}
          {member.twitter && (
            <a href={member.twitter} target="_blank" rel="noopener noreferrer" className="member-social" aria-label="Twitter">
              <i className="fa-brands fa-x-twitter"></i>
            </a>
          )}
        </div>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────
const Team = () => {
  const { t } = useTranslation();

  const { data: members, loading: loadingMembers } = useFetch(ENDPOINTS.team_members);
  const { data: values,  loading: loadingValues  } = useFetch(ENDPOINTS.teamValues);
  const { data: stats,   loading: loadingStats   } = useFetch(ENDPOINTS.teamStats);

  const TEAM_MEMBERS = members || [];
  const VALUES       = values  || [];
  const STATS        = stats   || [];

  const featured = TEAM_MEMBERS.filter((m) => m.featured);
  const rest     = TEAM_MEMBERS.filter((m) => !m.featured);

  const isLoading = loadingMembers || loadingValues || loadingStats;

  return (
    <div className="team-page">

      {/* ── Hero ── */}
      <section className="team-hero">
        <div className="team-hero-bg">
          <div className="team-orb team-orb--1"></div>
          <div className="team-orb team-orb--2"></div>
          <div className="team-hero-grid"></div>
        </div>
        <div className="team-hero-content">
          <span className="team-eyebrow">
            <i className="fa-solid fa-users"></i>
            {t("team.eyebrow")}
          </span>
          <h1 className="team-hero-title">
            {t("team.title")}
            <span className="team-hero-highlight"> {t("team.highlight")}</span>
          </h1>
          <p className="team-hero-subtitle">{t("team.subtitle")}</p>
        </div>
      </section>

      {/* ── Stats Bar ── */}
      <section className="team-stats">
        {isLoading ? (
          <div style={{ textAlign: "center", padding: "2rem", width: "100%" }}>
            <i className="fa-solid fa-spinner fa-spin" style={{ fontSize: "1.5rem", opacity: 0.5 }}></i>
          </div>
        ) : (
          STATS.map((s) => (
            <div key={s.id} className="team-stat-item">
              <span className="team-stat-value">{s.value}</span>
              <span className="team-stat-label">{s.label}</span>
            </div>
          ))
        )}
      </section>

      {/* ── Featured Founder ── */}
      {!isLoading && featured.length > 0 && (
        <section className="team-featured-section">
          <div className="section-header">
            <span className="section-eyebrow">{t("team.leadershipEyebrow")}</span>
            <h2 className="section-title">{t("team.leadershipTitle")}</h2>
          </div>
          <div className="team-featured-wrap">
            {featured.map((m) => (
              <MemberCard key={m.id} member={m} featured={true} founderLabel={t("team.founder")} />
            ))}
          </div>
        </section>
      )}

      {/* ── Rest of Team Grid ── */}
      {!isLoading && rest.length > 0 && (
        <section className="team-grid-section">
          <div className="section-header">
            <span className="section-eyebrow">{t("team.teamEyebrow")}</span>
            <h2 className="section-title">{t("team.teamTitle")}</h2>
            <p className="section-subtitle">{t("team.teamSubtitle")}</p>
          </div>
          <div className="team-grid team-grid--two">
            {rest.map((m) => (
              <MemberCard key={m.id} member={m} featured={false} founderLabel={t("team.founder")} />
            ))}
          </div>
        </section>
      )}

      {/* ── Loading state ── */}
      {isLoading && (
        <div style={{ textAlign: "center", padding: "8rem 0" }}>
          <i className="fa-solid fa-spinner fa-spin" style={{ fontSize: "2.5rem", opacity: 0.5 }}></i>
        </div>
      )}

      {/* ── Values ── */}
      {!isLoading && VALUES.length > 0 && (
        <section className="team-values">
          <div className="section-header">
            <span className="section-eyebrow">{t("team.valuesEyebrow")}</span>
            <h2 className="section-title">{t("team.valuesTitle")}</h2>
          </div>
          <div className="team-values-grid">
            {VALUES.map((v) => (
              <div key={v.id} className="team-value-card">
                <div className="team-value-icon">
                  <i className={v.icon}></i>
                </div>
                <h3 className="team-value-title">{v.title}</h3>
                <p className="team-value-desc">{v.desc}</p>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* ── Join Us CTA ── */}
      <section className="team-join-section">
        <div className="team-join-inner">
          <div className="join-orb"></div>
          <span className="section-eyebrow">
            <i className="fa-solid fa-handshake"></i>
            {t("team.joinEyebrow")}
          </span>
          <h2 className="team-join-title">{t("team.joinTitle")}</h2>
          <p className="team-join-sub">{t("team.joinSub")}</p>
          <div className="team-join-actions">
            <NavLink to="/contact" className="btn-primary">
              <i className="fa-solid fa-paper-plane"></i>
              {t("team.getInTouch")}
            </NavLink>
            <NavLink to="/portfolio" className="btn-secondary">
              {t("team.seeOurWork")}
              <i className="fa-solid fa-arrow-right"></i>
            </NavLink>
          </div>
        </div>
      </section>

    </div>
  );
};

export default Team;